import { useState } from 'react';
import { IconClockHour4, IconLockOpen, IconSearch, IconUserOff } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Button, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import './i18n';
import { CODE_LABEL, DELEGS, DEL_TASKS, ESCAL, TEAM, type TeamRow } from './tasks';
import s from './tasks.module.css';
import p from './people.module.css';

// Mint-Tasks · tab "Tôi đang thay" (khối isDelTab) và "Team" (khối isTeamTab). Chỉ đọc; nút ghi báo toast.

const norm = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const codeCls = (c: string) => (c === 'PRC' || c === 'DCF' ? s.cPri : c === 'TVA' ? s.cErr : '');
const toneCls = (t: string) => (t === 'err' ? s.dErr : t === 'warn' ? s.dWarn : '');

function Search({ q, setQ }: { q: string; setQ: (v: string) => void }) {
  const t = useT();
  return (
    <label className={s.search}>
      <IconSearch size={16} stroke={2.2} aria-hidden="true" />
      <input aria-label={t('pp.search')} placeholder={t('pp.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
    </label>
  );
}

function Body({ state, children, emptyTitle }: { state: ScreenState; children: React.ReactNode; emptyTitle: string }) {
  const t = useT();
  return (
    <StateView state={state} title={state === 'empty' ? emptyTitle : undefined} action={state === 'error' ? <Button onClick={() => location.reload()}>{t('tasks.retry')}</Button> : undefined} skeleton={<Skeleton rows={6} />}>
      {children}
    </StateView>
  );
}

export function DelegatesPane({ state }: { state: ScreenState }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: t('pp.noWrite') });
  const [q, setQ] = useState('');
  const [sel, setSel] = useState('');
  const tasks = DEL_TASKS.filter((x) => !q || norm(`${x.title} ${x.obj} ${x.sub}`).includes(norm(q)));
  return (
    <div className={s.page}>
      <div className={s.bar}>
        <Search q={q} setQ={setQ} />
        <button type="button" className={p.absent} onClick={noWrite}><IconUserOff size={16} aria-hidden="true" />{t('pp.absent')}</button>
        <div className={s.sums}><span>{t('pp.delegIn')} <b>{DELEGS.length}</b></span></div>
      </div>
      <Body state={state} emptyTitle={t('pp.emptyDel')}>
        <div className={p.cards}>
          {DELEGS.map((d) => (
            <section key={d.id} aria-label={d.k} className={p.card} data-sel={sel === d.id} onClick={() => { setSel(d.id); noWrite(); }}>
              <div className={p.cardHd}>
                <span className={p.av}>{d.ab}</span>
                <div><b>{t('pp.coverFor', { k: d.k })}</b><div>{d.src}</div></div>
                <span className={p.live}>{t('pp.live')}</span>
              </div>
              <dl><dt>{t('pp.scope')}</dt><dd>{d.scope}</dd><dt>{t('pp.time')}</dt><dd>{d.range}</dd><dt>{t('pp.apprRight')}</dt><dd>{d.appr}</dd></dl>
              <div className={p.cardFt}><span>{t('pp.inPeriod')} <b>{d.n}</b></span><span>{t('pp.overdue')} <b className={d.over ? s.err : undefined}>{d.over}</b></span><span>{t('pp.done')} <b>{d.doneN}</b></span></div>
            </section>
          ))}
        </div>
        <section className={s.grp} aria-label={t('pp.delTasks')}>
          <div className={p.secHd}><b title={t('pp.delTasksTip')}>{t('pp.delTasks')}</b></div>
          {tasks.map((x) => {
            const timed = /^(Còn|Quá)\s/.test(x.dueK); // dữ liệu mẫu
            return (
              <div key={x.id} className={s.row} onClick={noWrite}>
                <span className={`${s.code} ${codeCls(x.code)}`} title={CODE_LABEL[x.code] ?? x.code}>{x.code}</span>
                <div className={s.main}>
                  <div>{x.title}</div>
                  <div><a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>{x.obj}</a> · {x.sub}</div>
                </div>
                <span className={p.forK}>{t('pp.coverForLc', { k: x.forK })}</span>
                <div className={s.due} title={`${x.dueK} · ${x.due}`}>
                  <div className={toneCls(x.tone)}>{timed && <IconClockHour4 size={13} stroke={2} aria-hidden="true" />}{x.dueK}</div>
                  <div>{x.due}</div>
                </div>
              </div>
            );
          })}
        </section>
      </Body>
    </div>
  );
}

const ontColor = (v: number) => (v >= 85 ? 'var(--ok)' : v >= 75 ? 'var(--d-blue)' : 'var(--warn)');

export function TeamPane({ state }: { state: ScreenState }) {
  const t = useT();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [un, setUn] = useState<Record<string, true>>({});
  const rows = TEAM.filter((r) => !q || norm(`${r.k} ${r.seat}`).includes(norm(q)));
  const mx = Math.max(...TEAM.map((r) => r.open));
  const openN = TEAM.reduce((a, r) => a + r.open, 0);
  const overN = TEAM.reduce((a, r) => a + r.over, 0);
  const otP = TEAM.reduce((a, r) => a + r.open * r.ont, 0) / Math.max(1, openN) / 100;
  const low = [...TEAM].sort((a, b) => a.ont - b.ont)[0];
  const otPct = `${Math.round(otP * 100)}%`;
  const bars = TEAM.map((r) => ({ ...r, ok: Math.max(0, r.open - r.over - r.wait), w: `${Math.round((r.open / mx) * 100)}%` }));
  const aria = `${t('pp.openBy')}: ${bars.map((r) => t('pp.barAria', { k: r.k, open: r.open, over: r.over, wait: r.wait })).join('; ')}`;
  const unlock = (r: TeamRow) => { if (un[r.k]) return; setUn((m) => ({ ...m, [r.k]: true })); toast({ msg: `${t('pp.noWrite')} · ${r.k}` }); };
  return (
    <div className={s.page}>
      <div className={s.bar}>
        <Search q={q} setQ={setQ} />
        <div className={s.sums}><span>{t('pp.teamOver')} <b className={s.err}>7</b></span><span>{t('pp.onTime')} <b>84%</b></span></div>
      </div>
      <Body state={state} emptyTitle={t('pp.emptyTeam')}>
        <div className={p.two}>
          <section aria-label={t('pp.openBy')} className={p.panel}>
            <div className={p.panelHd}>
              <b>{t('pp.openBy')}</b>
              <span className={p.legend}><i style={{ background: 'var(--d-blue)' }} />{t('pp.inTime')}<i style={{ background: 'var(--err)' }} />{t('pp.overLc')}<i style={{ background: 'var(--d-gray)' }} />{t('pp.waitExt')}</span>
            </div>
            <div role="img" aria-label={aria}>
              {bars.map((r) => (
                <div key={r.k} className={p.barRow}>
                  <span>{r.k}</span>
                  <div className={p.track}><div style={{ width: r.w }}><div style={{ flex: `${r.ok} 1 0`, background: 'var(--d-blue)' }} /><div style={{ flex: `${r.over} 1 0`, background: 'var(--err)' }} /><div style={{ flex: `${r.wait} 1 0`, background: 'var(--d-gray)' }} /></div></div>
                  <span><b>{r.open}</b> {t('pp.openLc')} · <b className={r.over ? s.err : undefined}>{r.over}</b> {t('pp.lateLc')}</span>
                </div>
              ))}
            </div>
          </section>
          <section aria-label={t('pp.ot30')} className={p.panel}>
            <b>{t('pp.ot30team')}</b>
            <div className={p.gauge}>
              <svg role="img" aria-label={t('pp.gaugeAria', { pct: otPct, open: openN, over: overN })} width="170" height="93" viewBox="0 0 190 104">
                <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--line)" strokeWidth="14" strokeLinecap="round" />
                <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--d-blue)" strokeWidth="14" strokeLinecap="round" strokeDasharray="251.3 251.3" strokeDashoffset={251.3 * (1 - otP)} />
                <text x="95" y="82" textAnchor="middle" fontSize="28" fontWeight="550" fill="var(--tx)">{otPct}</text>
              </svg>
              <div><div><span>{t('pp.openTasks')}</span><b>{openN}</b></div><div><span>{t('pp.overdue')}</span><b className={s.err}>{overN}</b></div><div><span>{t('pp.lowest')}</span><b>{low ? `${low.k} ${low.ont}%` : '—'}</b></div></div>
            </div>
          </section>
        </div>
        <section aria-label={t('pp.load')} className={p.tbl}>
          <table>
            <thead><tr><th style={{ width: 220 }}>{t('pp.thPerson')}</th><th className={p.r} style={{ width: 110 }}>{t('pp.openTasks')}</th><th className={p.r} style={{ width: 110 }}>{t('pp.overdue')}</th><th className={p.r} style={{ width: 140 }}>{t('pp.waitOut')}</th><th>{t('pp.ot30')}</th><th style={{ width: 210 }}>{t('pp.gate')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.k}>
                  <td><b>{r.k}</b><div>{r.seat}</div></td>
                  <td className={p.r}>{r.open}</td>
                  <td className={p.r}><b className={r.over ? s.err : undefined}>{r.over}</b></td>
                  <td className={p.r}><span className={r.wait >= 5 ? p.hot : undefined}>{r.wait}</span>{r.wait >= 5 && <div className={s.warnTx}>{t('pp.abnormal')}</div>}</td>
                  <td><div className={p.ont}><div><div style={{ width: `${r.ont}%`, background: ontColor(r.ont) }} /></div><b>{r.ont}%</b></div></td>
                  <td>
                    {r.gate > 0
                      ? <button type="button" className={p.unlock} data-on={!!un[r.k]} title={t('pp.unlockTip')} onClick={() => unlock(r)}><IconLockOpen size={15} aria-hidden="true" />{un[r.k] ? t('pp.unlocked') : r.gate === 2 ? t('pp.unlockN', { n: r.gate }) : t('pp.unlock')}</button>
                      : <span className={p.free}>{t('pp.noBlock')}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section aria-label={t('pp.escal')} className={p.panel}>
          <b>{t('pp.escalUp')}</b>
          {ESCAL.map((e, i) => (
            <div key={e.code} className={p.esc} data-first={i === 0}>
              <span className={`${s.code} ${s.cErr}`}>{e.code}</span>
              <div><div>{e.k}</div><div>{e.path}</div></div>
              <span className={p.late}>{e.late}</span>
            </div>
          ))}
        </section>
      </Body>
    </div>
  );
}
